<script>
  /**
   * Ver, corregir o borrar un movimiento.
   *
   * Se abre tocando una fila de la lista. Editar tiene que ser tan fácil como
   * registrar: si corregir un error cuesta, la gente deja los errores ahí, y
   * unos pocos datos malos hacen que todos los números dejen de servir.
   */
  import { db, TIPO_MOVIMIENTO, METODO_PAGO, crearMovimiento, hoy } from '../lib/db.js';
  import { formatear, formatearAria, interpretar, calcularGmf } from '../lib/dinero.js';
  import { guardar, borrar } from '../lib/repositorio.js';
  import { obtenerFoto } from '../lib/comprobante.js';
  import { gmfDe, movimientoGmf } from '../lib/gmf.js';

  let {
    movimiento = $bindable(null),
    cuentas = [],
    categorias = [],
    entidades = [],
    alCambiar = () => {},
    alIrA = () => {},   // ({ tipo: 'deuda' | 'compra', id }): abre la pantalla dueña
  } = $props();

  /** El 4x1000 que cuelga de este movimiento, si lo tiene. */
  let hijosGmf = $state([]);
  let gmfManual = $state(null);

  let borrador = $state(null);
  let montoTexto = $state('');
  let guardando = $state(false);
  let confirmandoBorrado = $state(false);
  let foto = $state(null);
  let lineas = $state([]);

  const abierta = $derived(Boolean(movimiento));

  $effect(() => {
    if (!movimiento) {
      borrador = null;
      confirmandoBorrado = false;
      if (foto) { URL.revokeObjectURL(foto); foto = null; }
      lineas = [];
      return;
    }
    borrador = { ...movimiento };
    montoTexto = formatear(movimiento.monto, { simbolo: false });

    // Si vino de una foto, se muestra el comprobante: es la prueba de que el
    // dato no salió de la nada.
    if (movimiento.adjunto_id) {
      obtenerFoto(movimiento.adjunto_id).then((u) => (foto = u));
    }
    // Y si la factura traía productos, se listan.
    db.lineas.where('movimiento_id').equals(movimiento.id).toArray()
      .then((l) => (lineas = l.filter((x) => !x.borrado_en)));

    // El 4x1000: si ya tenía, se respeta lo que se cobró de verdad; si la
    // persona dijo "no me lo cobraron", también. Si no, lo calcula la app.
    const original = movimiento;
    gmfManual = null;
    hijosGmf = [];
    db.movimientos.where('movimiento_padre_id').equals(original.id).toArray().then((h) => {
      hijosGmf = h.filter((x) => !x.borrado_en && x.concepto === '4x1000');
      const auto = gmfDe({
        tipo: original.tipo, monto: original.monto,
        cuentaOrigen: cuentas.find((c) => c.id === original.cuenta_origen),
        entidad: entidadDe(original.cuenta_origen), metodoPago: original.metodo_pago,
      }).monto;
      if (hijosGmf.length && hijosGmf[0].monto !== auto) gmfManual = hijosGmf[0].monto;
      else if (!hijosGmf.length && auto > 0 && original.gmf_revisado) gmfManual = 0;
    });
  });

  function entidadDe(cuentaId) {
    const c = cuentas.find((x) => x.id === cuentaId);
    return entidades.find((e) => e.id === c?.entidad_id) ?? null;
  }

  /**
   * ¿Este movimiento es parte de algo más grande?
   *
   * Un movimiento de un préstamo o de una compra a cuotas no se corrige
   * aquí: si se cambiara el monto aquí, el préstamo seguiría diciendo el
   * monto viejo y el saldo quedaría descuadrado — justo lo que pasaba antes.
   * Se corrige en su pantalla, que ajusta todo junto.
   */
  const dueno = $derived(
    !borrador ? null
    : borrador.deuda_id ? {
        tipo: 'deuda', id: borrador.deuda_id, boton: 'Abrir el préstamo',
        texto: borrador.contraparte
          ? `Es parte del préstamo con ${borrador.contraparte}.`
          : 'Es parte de un préstamo.',
      }
    : borrador.compra_diferida_id ? {
        tipo: 'compra', id: borrador.compra_diferida_id, boton: 'Abrir la compra',
        texto: 'Es parte de una compra a cuotas.',
      }
    : borrador.pago_tarjeta ? {
        tipo: 'compra', id: null, boton: 'Abrir las cuotas',
        texto: 'Es el pago de la factura de una tarjeta: está repartido entre sus compras. ' +
               'Se corrige o se deshace desde Cuotas.',
      }
    : null
  );

  /** Este movimiento ES un 4x1000: no tiene 4x1000 propio. */
  const esGmf = $derived(Boolean(borrador?.movimiento_padre_id) && borrador?.concepto === '4x1000');

  const montoEditado = $derived(Math.abs(interpretar(montoTexto) ?? 0));
  const cuentaQueSale = $derived(
    borrador && borrador.tipo !== TIPO_MOVIMIENTO.INGRESO
      ? cuentas.find((c) => c.id === borrador.cuenta_origen) ?? null
      : null
  );
  const gmfAuto = $derived(
    borrador && !esGmf
      ? gmfDe({
          tipo: borrador.tipo, monto: montoEditado, cuentaOrigen: cuentaQueSale,
          entidad: entidadDe(borrador.cuenta_origen), metodoPago: borrador.metodo_pago,
        })
      : { monto: 0, razon: null }
  );
  const gmf = $derived(esGmf ? 0 : (gmfManual ?? gmfAuto.monto));

  const esIngreso = $derived(borrador?.tipo === TIPO_MOVIMIENTO.INGRESO);
  const esTransferencia = $derived(borrador?.tipo === TIPO_MOVIMIENTO.TRANSFERENCIA);

  const categoriasVisibles = $derived(
    categorias.filter(
      (c) => c.tipo === (esIngreso ? 'ingreso' : 'egreso') && c.padre_id && !c.archivada
    )
  );

  const ORIGENES = {
    manual: 'Escrito a mano',
    ocr: 'Leído de una foto',
    voz: 'Dictado',
    texto_libre: 'Escrito en texto libre',
    importado: 'Traído de Excel',
    recurrente: 'Generado automáticamente',
    automatico: 'Generado por la app',
  };

  const METODOS = [
    [METODO_PAGO.EFECTIVO, 'Efectivo'],
    [METODO_PAGO.TRANSFERENCIA, 'Transferencia'],
    [METODO_PAGO.PSE, 'PSE'],
    [METODO_PAGO.QR, 'QR'],
    [METODO_PAGO.DEBITO, 'Débito'],
    [METODO_PAGO.CREDITO, 'Crédito'],
    [METODO_PAGO.DEBITO_AUTOMATICO, 'Débito automático'],
  ];

  function cerrar() {
    movimiento = null;
  }

  /**
   * <dialog> nativo, igual que la hoja de registro: atrapa el foco, cierra
   * con Escape y oscurece el fondo sin escribir nada de eso a mano. Antes era
   * un <section role="dialog"> con un velo aparte, que no atrapaba el foco.
   */
  let dialogo = $state(null);
  $effect(() => {
    if (!dialogo) return;
    if (abierta && !dialogo.open) dialogo.showModal();
    if (!abierta && dialogo.open) dialogo.close();
  });

  async function aplicar() {
    if (guardando || !borrador) return;
    const monto = interpretar(montoTexto);
    if (!monto || Math.abs(monto) === 0) return;

    guardando = true;
    try {
      const editado = {
        ...borrador,
        monto: Math.abs(monto),
        ...(esGmf ? {} : { gmf, gmf_revisado: true }),
      };
      await guardar('movimientos', editado);

      // El 4x1000 sigue al movimiento: si cambió el monto o la cuenta, se
      // corrige; si ya no aplica, se quita; si ahora aplica, se crea. Antes
      // editar un movimiento dejaba el impuesto viejo colgando.
      if (!esGmf) {
        if (gmf > 0) {
          if (hijosGmf.length) {
            await guardar('movimientos', {
              ...hijosGmf[0], ...movimientoGmf(editado, gmf), id: hijosGmf[0].id,
            });
            for (const x of hijosGmf.slice(1)) await borrar('movimientos', x.id);
          } else {
            await guardar('movimientos', crearMovimiento(movimientoGmf(editado, gmf)));
          }
        } else {
          for (const x of hijosGmf) await borrar('movimientos', x.id);
        }
      }

      alCambiar();
      cerrar();
    } finally {
      guardando = false;
    }
  }

  /**
   * Borra el movimiento y, si tenía un 4x1000 asociado, también ese.
   * Dejar huérfano el cobro del impuesto dejaría el saldo mal por una cifra
   * pequeña, que es justo el tipo de error que no se encuentra nunca.
   */
  async function borrarTodo() {
    if (guardando || !movimiento) return;
    guardando = true;
    try {
      const hijos = await db.movimientos
        .where('movimiento_padre_id').equals(movimiento.id).toArray();
      for (const h of hijos) await borrar('movimientos', h.id);

      for (const l of lineas) await borrar('lineas', l.id);

      await borrar('movimientos', movimiento.id);
      alCambiar();
      cerrar();
    } finally {
      guardando = false;
    }
  }
</script>

<dialog
  bind:this={dialogo}
  class="hoja"
  aria-label="Detalle del movimiento"
  onclose={cerrar}
  onclick={(e) => { if (e.target === dialogo) cerrar(); }}
>
  {#if abierta && borrador}
    <button class="agarradera" onclick={cerrar} aria-label="Cerrar"></button>

    {#if dueno}
      <div class="cuerpo">
        <p class="monto-fijo cifra">{formatear(borrador.monto)}</p>
        <p class="dato">
          {borrador.concepto || 'Movimiento'} · {new Date(borrador.fecha + 'T12:00:00')
            .toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
        <div class="ligado">
          <p><strong>{dueno.texto}</strong></p>
          <p>
            Para que el saldo no quede descuadrado, la fecha, el monto o la
            cuenta se corrigen desde allá: la app ajusta todo junto, incluido
            el 4x1000.
          </p>
          <button class="principal" onclick={() => { const d = dueno; cerrar(); alIrA(d); }}>
            {dueno.boton}
          </button>
        </div>
      </div>
    {:else}
    <div class="cuerpo">
      <label>
        <span class="etiqueta">Monto</span>
        <input
          class="monto cifra"
          type="text"
          inputmode="numeric"
          bind:value={montoTexto}
          aria-label="Monto"
        />
        <span class="solo-lectores">{formatearAria(borrador.monto)}</span>
      </label>

      <div class="tipos">
        {#each [[TIPO_MOVIMIENTO.EGRESO, 'Gasto'], [TIPO_MOVIMIENTO.INGRESO, 'Ingreso'], [TIPO_MOVIMIENTO.TRANSFERENCIA, 'Traslado']] as [valor, etiqueta]}
          <button
            class="tipo"
            class:activo={borrador.tipo === valor}
            data-tipo={valor}
            onclick={() => { borrador.tipo = valor; }}
          >{etiqueta}</button>
        {/each}
      </div>

      <div class="par">
        <label>
          <span class="etiqueta">Fecha</span>
          <input type="date" bind:value={borrador.fecha} max={hoy()} />
        </label>
        <label>
          <span class="etiqueta">Hora</span>
          <input type="time" bind:value={borrador.hora} />
        </label>
        <label>
          <span class="etiqueta">{esIngreso ? 'Entra a' : 'Sale de'}</span>
          <select
            value={esIngreso ? borrador.cuenta_destino : borrador.cuenta_origen}
            onchange={(e) => {
              if (esIngreso) borrador.cuenta_destino = e.target.value;
              else borrador.cuenta_origen = e.target.value;
            }}
          >
            {#each cuentas as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
          </select>
        </label>
      </div>

      {#if esTransferencia}
        <label>
          <span class="etiqueta">Entra a</span>
          <select bind:value={borrador.cuenta_destino}>
            {#each cuentas as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
          </select>
        </label>
      {:else}
        <label>
          <span class="etiqueta">Categoría</span>
          <select bind:value={borrador.categoria_id}>
            <option value={null}>Sin categoría</option>
            {#each categoriasVisibles as c}<option value={c.id}>{c.nombre}</option>{/each}
          </select>
        </label>
      {/if}

      <label>
        <span class="etiqueta">Concepto</span>
        <input type="text" bind:value={borrador.concepto} placeholder="En qué fue" />
      </label>

      <label>
        <span class="etiqueta">Dónde</span>
        <input type="text" bind:value={borrador.contraparte} placeholder="Establecimiento o persona" />
      </label>

      {#if !esIngreso}
        <label>
          <span class="etiqueta">Método de pago</span>
          <select bind:value={borrador.metodo_pago}>
            {#each METODOS as [valor, etiqueta]}<option value={valor}>{etiqueta}</option>{/each}
          </select>
        </label>
      {/if}

      <!-- El 4x1000, visible y corregible también al editar. -->
      {#if !esGmf && cuentaQueSale && montoEditado > 0}
        <div class="gmf">
          {#if gmf > 0}
            <span>4x1000: <strong class="cifra">{formatear(gmf)}</strong></span>
            <button class="enlace" onclick={() => (gmfManual = 0)}>No me lo cobraron</button>
          {:else}
            <span class="sin-gmf">
              Sin 4x1000
              {#if gmfManual === 0}(dijiste que no te lo cobraron){:else if gmfAuto.razon}({gmfAuto.razon}){/if}
            </span>
            {#if gmfManual === 0 && gmfAuto.monto > 0}
              <button class="enlace" onclick={() => (gmfManual = null)}>Sí me lo cobraron</button>
            {:else if gmfAuto.monto === 0 && cuentaQueSale.tipo !== 'efectivo'}
              <button class="enlace" onclick={() => (gmfManual = calcularGmf(montoEditado))}>Sí me lo cobraron</button>
            {/if}
          {/if}
        </div>
      {/if}
      {#if esGmf}
        <p class="dato">Es el 4x1000 de otro movimiento. Si el banco cobró otra cifra, corrígela aquí.</p>
      {/if}

      {#if lineas.length}
        <section class="productos">
          <h3>Los {lineas.length} productos de la factura</h3>
          {#each lineas as l}
            <div class="producto">
              <span>{l.concepto}</span>
              <span class="cifra">{formatear(l.monto)}</span>
            </div>
          {/each}
        </section>
      {/if}

      {#if foto}
        <details>
          <summary>Ver el comprobante</summary>
          <img src={foto} alt="El comprobante de este movimiento" />
        </details>
      {/if}

      <p class="procedencia">
        {ORIGENES[borrador.origen_dato] ?? borrador.origen_dato}
        {#if borrador.referencia}· ref. {borrador.referencia}{/if}
      </p>
    </div>

    <!-- Borrar va abajo y aparte, con confirmación. No se puede deshacer. -->
    {#if !confirmandoBorrado}
      <div class="acciones">
        <button class="borrar" onclick={() => (confirmandoBorrado = true)}>Borrar</button>
        <button class="principal" disabled={guardando} onclick={aplicar}>
          {guardando ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    {:else}
      <div class="confirmar">
        <p>¿Seguro? Esto no se puede deshacer.</p>
        <div class="acciones">
          <button class="secundario" onclick={() => (confirmandoBorrado = false)}>No, cancelar</button>
          <button class="borrar-firme" disabled={guardando} onclick={borrarTodo}>
            {guardando ? 'Borrando…' : 'Sí, borrar'}
          </button>
        </div>
      </div>
    {/if}
    {/if}
  {/if}
</dialog>

<style>
  /* El fondo oscuro lo pinta el navegador con ::backdrop: ya no hay velo. */
  .hoja::backdrop {
    background: var(--velo);
    animation: aparecer var(--d-rapida) var(--ease-entrada);
  }
  @keyframes aparecer { from { opacity: 0 } to { opacity: 1 } }

  .hoja {
    /* El <dialog> se centra solo; aquí va pegado abajo, al alcance del pulgar. */
    margin: auto auto 0;
    border: none;
    color: var(--ink-1);
    width: 100%;
    max-width: var(--w-app);
    max-height: 92dvh;
    overflow-y: auto;
    background: var(--bg-surface);
    border-radius: var(--r-2xl) var(--r-2xl) 0 0;
    box-shadow: var(--sombra-hoja);
    padding: var(--e-2) var(--e-4) calc(var(--e-4) + var(--safe-b));
  }
  .hoja[open] { animation: subir var(--d-normal) var(--ease-entrada) }

  /* ── Movimiento que pertenece a un préstamo o una compra a cuotas ── */
  .monto-fijo {
    font-size: var(--t-2xl);
    font-weight: var(--w-bold);
    text-align: center;
    margin-top: var(--e-2);
  }
  .ligado {
    display: flex;
    flex-direction: column;
    gap: var(--e-2);
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    border-left: 3px solid var(--acento);
    font-size: var(--t-sm);
    line-height: var(--lh-loose);
    color: var(--ink-2);
  }

  /* ── El 4x1000 al editar ── */
  .gmf {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--e-2);
    flex-wrap: wrap;
    font-size: var(--t-sm);
    padding: var(--e-2) var(--e-3);
    background: var(--bg-sunken);
    border-radius: var(--r-md);
  }
  .sin-gmf { color: var(--ink-3) }
  .enlace {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    color: var(--acento);
    text-decoration: underline;
    text-underline-offset: 2px;
    min-height: 32px;
  }
  @keyframes subir {
    from { transform: translateY(100%) }
    to   { transform: translateY(0) }
  }

  .agarradera {
    width: 40px;
    height: 4px;
    border-radius: var(--r-full);
    background: var(--border-strong);
    margin: var(--e-1) auto var(--e-3);
    opacity: 0.5;
    display: block;
  }

  .cuerpo { display: flex; flex-direction: column; gap: var(--e-3) }

  label { display: flex; flex-direction: column; gap: var(--e-1) }
  .etiqueta { font-size: var(--t-sm); font-weight: var(--w-medium) }
  .par { display: grid; grid-template-columns: 1fr 1fr; gap: var(--e-3) }

  input[type='text'],
  input[type='date'],
  select {
    padding: var(--e-3);
    font-size: var(--t-base);
    background: var(--bg-sunken);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    min-height: var(--tap);
    width: 100%;
  }

  .monto {
    font-size: var(--t-2xl);
    font-weight: var(--w-bold);
    text-align: center;
  }

  .tipos {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--e-1);
    background: var(--bg-sunken);
    padding: var(--e-1);
    border-radius: var(--r-full);
  }
  .tipo {
    padding: var(--e-2);
    border-radius: var(--r-full);
    font-size: var(--t-sm);
    font-weight: var(--w-semibold);
    color: var(--ink-2);
  }
  .tipo.activo { background: var(--bg-surface); color: var(--ink-1); box-shadow: var(--sombra-1) }
  .tipo.activo[data-tipo='ingreso']       { color: var(--ingreso) }
  .tipo.activo[data-tipo='transferencia'] { color: var(--transferencia) }

  .dato {
    font-size: var(--t-xs);
    color: var(--ink-3);
    line-height: var(--lh-loose);
    padding: var(--e-2) var(--e-3);
    background: var(--bg-sunken);
    border-radius: var(--r-md);
  }

  .productos { gap: var(--e-1); display: flex; flex-direction: column }
  h3 {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--ink-3);
  }
  .producto {
    display: flex;
    justify-content: space-between;
    gap: var(--e-2);
    font-size: var(--t-sm);
    padding: var(--e-1) 0;
    border-bottom: 1px solid var(--border);
  }

  details summary {
    font-size: var(--t-sm);
    color: var(--acento);
    cursor: pointer;
    padding: var(--e-2) 0;
  }
  details img { width: 100%; border-radius: var(--r-md); border: 1px solid var(--border) }

  .procedencia {
    font-size: var(--t-2xs);
    color: var(--ink-3);
    text-align: center;
    padding-top: var(--e-2);
  }

  .acciones { display: flex; gap: var(--e-2); margin-top: var(--e-3) }
  .principal {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--acento);
    color: var(--on-acento);
    font-weight: var(--w-semibold);
    min-height: var(--tap);
  }
  .principal:disabled { opacity: 0.4 }
  .secundario {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    font-weight: var(--w-medium);
    min-height: var(--tap);
  }
  .borrar {
    padding: var(--e-3) var(--e-4);
    border-radius: var(--r-md);
    color: var(--peligro);
    background: var(--peligro-suave);
    font-weight: var(--w-medium);
    min-height: var(--tap);
  }
  .borrar-firme {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--peligro);
    color: var(--on-peligro);
    font-weight: var(--w-semibold);
    min-height: var(--tap);
  }

  .confirmar { margin-top: var(--e-3) }
  .confirmar p {
    font-size: var(--t-sm);
    color: var(--peligro);
    text-align: center;
    margin-bottom: var(--e-1);
  }
</style>
